import React, { useState, useEffect } from 'react';
import { Box, Table, Text, Grid, Spacer } from '@flexisaf/flexibull2';
import styled from 'styled-components';
import { formatNumber } from 'utils/formatNumber';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import PropTypes from 'prop-types';
import Theme from 'utils/theme';
import { currencyTags, EMPTY_VALUE, FALL_BACK_TZ } from 'utils/constants';
import EmptyCourse from 'assets/images/empty_course.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadingTransactions,
  selectLoadedTransactions,
} from 'redux/applicants/selector';
import { fetchCandidateTransactions } from 'redux/applicants/actions';

import Remita from '../../../assets/images/remita.svg';
import Paystack from '../../../assets/images/paystack.svg';
import Interswitch from '../../../assets/images/interswitch.svg';
import Stripe from '../../../assets/images/stripe.png';
import {
  statustag,
  checkTransactionStatus,
} from 'components/transactions/transactionTable';
import { PDFDownloadLink } from '@react-pdf/renderer';
import TransactionPdf from '../PdfDownloads/TransactionPdf';
import { selectInstitution } from 'redux/configurations/selectors';
import { calculateDateOffset } from 'utils/helpers';

const moment = require('moment-timezone');

const CollapsibleBox = styled.div`
  td {
    i {
      cursor: pointer;
    }
  }
  & .expanded {
    background-color: rgba(229, 229, 229, 0.3) !important;
    td {
      box-shadow: inset 0 3px 6px -3px rgba(0, 0, 0, 0.2);
      padding: 0;
      & .moreInfo {
        overflow: hidden;
      }
    }
    &:hover {
      background-color: none;
    }
  }
`;

const TransactionTab = ({ modalSize, applicant }) => {
  const dispatch = useDispatch();
  const institution = useSelector(selectInstitution);
  const transactions = useSelector(selectLoadedTransactions);
  const loading = useSelector(loadingTransactions);
  const organisation = useSelector(selectInstitution);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleExpandRow = (key) => {
    if (key === expandedRow) {
      setExpandedRow(null);
    } else {
      setExpandedRow(key);
    }
  };

  const checkPaymentPlatform = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'remita':
        return <img height="16px" src={Remita} alt="Remita Logo" />;
      case 'paystack':
        return <img height="16px" src={Paystack} alt="Paystack Logo" />;
      case 'interswitch':
        return <img height="16px" src={Interswitch} alt="Interswitch Logo" />;
      case 'stripe':
        return <img height="20px" src={Stripe} alt="Stripe Logo" />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (applicant?.id) {
      dispatch(
        fetchCandidateTransactions({
          page: 1,
          size: 10,
          candidateId: applicant?.id,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant]);

  if (loading) {
    return (
      <Box width="100%" vAlign>
        <Box width="100%" margin="30px 0" align="center">
          <SkeletonTheme color="#d0d5d933" highlightColor="#c2cad133">
            <Skeleton count={5} height={50} />
          </SkeletonTheme>
        </Box>
      </Box>
    );
  }

  return (
    <Box margin="0 40px 0 0">
      {transactions?.length === 0 ? (
        <Box vAlign height="70vh">
          <Box width="200px" margin="0 auto" align="center">
            <img src={EmptyCourse} alt="Empty Transaction" />
            <Spacer />
            <Text>No Transactions found</Text>
            <Spacer />
          </Box>
        </Box>
      ) : (
        <CollapsibleBox>
          <Table>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Payer's Email</th>
                  {modalSize && <th>User Account</th>}
                  <th>RRR</th>
                  <th>Amount</th>
                  {modalSize && <th>Platform</th>}
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.map((d, i) => (
                  <>
                    <tr key={d?.reference}>
                      <td onClick={() => handleExpandRow(i)}>
                        <i
                          className={
                            expandedRow === i ? `saf-up-open` : `saf-down-open`
                          }
                        />
                      </td>
                      <td>
                        <Text color={Theme.PrimaryColor}>
                          {d?.email || EMPTY_VALUE}
                        </Text>
                      </td>
                      {modalSize && <td>{d?.username || EMPTY_VALUE}</td>}
                      <td>{d?.rrr || EMPTY_VALUE}</td>
                      <td>
                        {currencyTags[d?.currency || 'NGN']}
                        {formatNumber(d?.amount + d?.gatewayCharge || 0)}
                      </td>
                      {modalSize && (
                        <td>
                          {checkPaymentPlatform(d?.platform.toLowerCase())}
                        </td>
                      )}
                      <td>
                        {moment(
                          calculateDateOffset(
                            d?.createdAt,
                            institution?.timeZone
                          )
                        )
                          .tz(institution?.timeZone || FALL_BACK_TZ)
                          .format('MMMM Do YYYY')}
                      </td>
                      <td>
                        {statustag(
                          checkTransactionStatus(d?.status.toLowerCase())
                        )}
                      </td>
                      <td>
                        <PDFDownloadLink
                          document={
                            <TransactionPdf
                              data={{
                                data: d,
                                applicant: applicant,
                                org: organisation,
                                timezone: institution?.timeZone,
                              }}
                            />
                          }
                          fileName={`${moment(
                            calculateDateOffset(
                              d?.createdAt,
                              institution?.timeZone
                            )
                          )
                            .tz(institution?.timeZone || FALL_BACK_TZ)
                            .format('MMMM Do YYYY')} Transaction`}
                        >
                          {({ loading }) =>
                            loading ? (
                              <i className="saf-spin animate-spin" />
                            ) : (
                              <i className="saf-download" />
                            )
                          }
                        </PDFDownloadLink>
                      </td>
                    </tr>
                    {expandedRow === i && (
                      <tr className="expanded">
                        <td colSpan={modalSize ? 9 : 7}>
                          <div className="moreInfo">
                            <Grid gap="0px 30px" default="1fr">
                              <Box margin="15px">
                                <Grid gap="20px 10px" default="1fr">
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      size="13px"
                                      color={Theme.PrimaryInputOutline}
                                      block
                                    >
                                      Payers Information
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {d?.name}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      size="11px"
                                      color={Theme.PrimaryInputOutline}
                                      block
                                    >
                                      Transaction Reference
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {d?.reference}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      size="13px"
                                      color={Theme.PrimaryInputOutline}
                                      block
                                    >
                                      Application
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {applicant?.applicationTitle}
                                    </Text>
                                    <Spacer space="5px" />
                                    <Text capitalize block>
                                      {applicant?.course?.code} -{' '}
                                      {applicant?.course?.title}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      size="13px"
                                      color={Theme.PrimaryInputOutline}
                                      block
                                    >
                                      Payers Email
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {d?.email}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      size="13px"
                                      color={Theme.PrimaryInputOutline}
                                      block
                                    >
                                      Time Stamp
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {moment(
                                        calculateDateOffset(
                                          d?.createdAt,
                                          institution?.timeZone
                                        )
                                      )
                                        .tz(
                                          institution?.timeZone || FALL_BACK_TZ
                                        )
                                        .format('MMMM Do YYYY, h:mm:ss a')}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      color={Theme.PrimaryInputOutline}
                                      size="11px"
                                      block
                                    >
                                      RRR
                                    </Text>
                                    <Spacer />
                                    <Text capitalize block>
                                      {d?.rrr || EMPTY_VALUE}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      uppercase
                                      bold
                                      color={Theme.PrimaryInputOutline}
                                      size="13px"
                                      style={{ marginRight: 10 }}
                                      block
                                    >
                                      {' '}
                                      Platform
                                    </Text>
                                    <Text capitalize block size="25px">
                                      {checkPaymentPlatform(
                                        d?.platform.toLowerCase()
                                      )}
                                    </Text>
                                  </Box>
                                </Grid>
                              </Box>
                              <Box>
                                <Box
                                  round="5px"
                                  style={{
                                    boxShadow:
                                      '0px 0px 12px rgba(157, 157, 183, 0.8)',
                                  }}
                                  margin="15px"
                                  pad="1px 0 0"
                                  background="#fff"
                                >
                                  {d?.items
                                    .filter((fee) => fee.amount !== 0)
                                    .map((fee, i) => (
                                      <Box
                                        key={i}
                                        display="flex"
                                        style={{
                                          justifyContent: 'space-between',
                                        }}
                                        margin="25px 20px 0"
                                      >
                                        <Text capitalize block>
                                          {fee.name}
                                        </Text>
                                        <Text capitalize block bold size="14px">
                                          {currencyTags[fee?.currency || 'NGN']}
                                          {formatNumber(fee.amount)}
                                        </Text>
                                      </Box>
                                    ))}
                                    {d?.gatewayCharge !== 0 && (
                                      <Box
                                        display="flex"
                                        style={{ justifyContent: 'space-between' }}
                                        margin="25px 20px 0"
                                      >
                                        <Text capitalize block>
                                          Payment Gateway Fee
                                        </Text>
                                        <Text capitalize block bold size="14px">
                                          {currencyTags[d?.currency || 'NGN']}{' '}
                                          {formatNumber(d?.gatewayCharge)}
                                        </Text>
                                      </Box>
                                    )}
                                  <Box
                                    margin="20px 0 0"
                                    background={Theme.PrimaryFade}
                                    height="1px"
                                  />
                                  <Box
                                    background={Theme.PrimaryFade}
                                    pad="25px 20px"
                                    display="flex"
                                    style={{ justifyContent: 'space-between' }}
                                  >
                                    <Text uppercase block>
                                      Total Amount
                                    </Text>
                                    <Text
                                      capitalize
                                      color={Theme.PrimaryGreen}
                                      block
                                      bold
                                      size="17px"
                                    >
                                      {currencyTags[d?.currency || 'NGN']}
                                      {formatNumber(d?.amount + d?.gatewayCharge || 0)}
                                    </Text>
                                  </Box>
                                </Box>
                              </Box>
                            </Grid>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </Table>
        </CollapsibleBox>
      )}
    </Box>
  );
};

export default TransactionTab;

TransactionTab.propTypes = {
  modalSize: PropTypes.bool,
  applicant: PropTypes.object,
};
